import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { TaskUpdateResponse } from '~/services/api/tasks';
import * as tasksApi from '~/services/api/tasks';

import type { ProjectWithTasks, TaskWithSubtasks, UpdateTaskPayload } from '~/types';

import { logger } from '../utils/logger';

import { useAuth } from './useAuth';
import { useTenant } from './useTenant';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useTasks(projectId: string | null) {
  const { loading, user } = useAuth();
  const { isTenantReady } = useTenant();
  const queryClient = useQueryClient();

  const {
    data: tasks = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ['tasks', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [];
      }
      const fetched = await tasksApi.getProjectTasks(projectId);
      // A temp-* draft row (see handleTaskCreate in useTaskOperations.ts) lives only in
      // this cache entry, never on the server — any refetch here, including ones
      // triggered by SSE-driven invalidation, must preserve it or the open "Add Task"
      // input silently disappears mid-edit.
      // Pattern: docs/design-patterns/frontend/state-management.md (single-invalidation-strategy)
      const draftTasks = (
        queryClient.getQueryData<TaskWithSubtasks[]>(['tasks', projectId]) ?? []
      ).filter(t => t.id.startsWith('temp-'));
      return draftTasks.length > 0 ? [...fetched, ...draftTasks] : fetched;
    },
    enabled: !loading && !!user && !!projectId && !projectId.startsWith('temp-') && isTenantReady,
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes - prevents spam refetches
    refetchOnWindowFocus: false, // Don't auto-refetch - user can manually refresh
    refetchOnMount: false, // Don't refetch on component mount
    refetchOnReconnect: false, // Don't refetch when reconnecting - prevents spam when flaky
  });

  const createTask = useMutation({
    mutationFn: tasksApi.createTask,
    onSuccess: (newTask): void => {
      queryClient.setQueryData(['tasks', newTask.projectId], (old: TaskWithSubtasks[] = []) => {
        // Find and replace any temporary task, or append if none found
        const tempTask = old.find(t => t.id.startsWith('temp-'));
        if (tempTask) {
          return old.map(t =>
            t.id === tempTask.id ? { ...newTask, isEditing: false, subtasks: [] } : t,
          );
        }
        return [...old, newTask];
      });

      // Refresh project counts (totalTasks/completedTasks)
      void queryClient.invalidateQueries({ queryKey: ['project', newTask.projectId] });
    },
  });

  const updateTask = useMutation({
    mutationFn: async ({
      projectId,
      taskId,
      updates,
    }: {
      projectId: string;
      taskId: string;
      updates: UpdateTaskPayload;
    }) => {
      logger.debug({ component: 'useTasks' }, 'Update task mutation starting', {
        projectId,
        taskId,
        updates,
      });
      try {
        const result = await tasksApi.updateTask(projectId, taskId, updates);
        logger.debug({ component: 'useTasks' }, 'Update task mutation completed', {
          projectId,
          taskId,
          result,
        });
        return result;
      } catch (error) {
        logger.error({ component: 'useTasks' }, 'Update task mutation failed', error as Error, {
          projectId,
          taskId,
        });
        throw error;
      }
    },
    onMutate: async ({ projectId, taskId, updates }) => {
      logger.debug({ component: 'useTasks' }, 'Starting optimistic update', {
        projectId,
        taskId,
        updates,
      });

      // Snapshot the previous values first
      const previousTasks =
        queryClient.getQueryData<TaskWithSubtasks[]>(['tasks', projectId]) || [];
      const previousProjects = queryClient.getQueryData<ProjectWithTasks[]>(['projects']) || [];

      // Cancel any outgoing refetches
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        logger.warn({ component: 'useTasks' }, 'Task not found for mutation', {
          taskId,
        });
        // Return context even if task not found (for rollback)
        return { previousTasks, previousProjects, projectId };
      }

      await queryClient.cancelQueries({ queryKey: ['tasks', task.projectId] });
      await queryClient.cancelQueries({ queryKey: ['projects'] });

      // Optimistically update to the new value
      const updatedTask = { ...task, ...updates };
      logger.debug({ component: 'useTasks' }, 'Applying optimistic update', {
        taskId: taskId,
        updates,
        updatedTask,
      });

      queryClient.setQueryData(['tasks', task.projectId], (old: TaskWithSubtasks[] = []) =>
        old.map(t => (t.id === taskId ? updatedTask : t)),
      );

      // Return a context with the snapshotted values
      return { previousTasks, previousProjects, projectId: task.projectId };
    },
    onSuccess: (response: TaskUpdateResponse): void => {
      logger.debug({ component: 'useTasks' }, 'Update task mutation succeeded', { response });
      const { project, task } = response;

      // Update task cache
      queryClient.setQueryData(['tasks', task.projectId], (old: TaskWithSubtasks[] = []) =>
        old.map(t => (t.id === task.id ? task : t)),
      );

      // Update project cache with new task counts
      if (project) {
        queryClient.setQueryData(['projects'], (old: ProjectWithTasks[] = []) =>
          old.map(p => (p.id === project.id ? project : p)),
        );
      }
    },
    onError: (
      err,
      { taskId },
      context?: {
        previousProjects: ProjectWithTasks[];
        previousTasks: TaskWithSubtasks[];
        projectId: string;
      },
    ): void => {
      logger.error({ component: 'useTasks' }, 'Update task mutation error', err, { taskId });
      // If the mutation fails, use the context to roll back
      if (context) {
        logger.debug({ component: 'useTasks' }, 'Rolling back optimistic update', {
          projectId: context.projectId,
        });
        queryClient.setQueryData(['tasks', context.projectId], context.previousTasks);
        queryClient.setQueryData(['projects'], context.previousProjects);
      }
    },
  });

  const deleteTask = useMutation({
    mutationFn: ({ projectId, taskId }: { projectId: string; taskId: string }) =>
      tasksApi.deleteTask(projectId, taskId),
    onMutate: async ({ projectId, taskId }) => {
      const task = tasks.find(t => t.id === taskId);

      // Snapshot the previous values first (before early return)
      const previousTasks =
        queryClient.getQueryData<TaskWithSubtasks[]>(['tasks', projectId]) || [];

      if (!task) {
        return { previousTasks, projectId };
      }

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', task.projectId] });

      // Optimistically update to the new value
      queryClient.setQueryData(['tasks', task.projectId], (old: TaskWithSubtasks[] = []) =>
        old.filter(t => t.id !== taskId),
      );

      return { previousTasks, projectId: task.projectId };
    },
    onError: (
      _err,
      _id,
      context?: {
        previousTasks: TaskWithSubtasks[];
        projectId: string;
      },
    ): void => {
      // If the mutation fails, use the context to roll back
      if (context) {
        queryClient.setQueryData(['tasks', context.projectId], context.previousTasks);
      }
    },
    onSuccess: (_, { projectId }): void => {
      // Only invalidate on SUCCESS to sync with server
      void queryClient.invalidateQueries({
        queryKey: ['tasks', projectId],
      });
      // Refresh project counts (totalTasks/completedTasks)
      void queryClient.invalidateQueries({
        queryKey: ['project', projectId],
      });
    },
  });

  return {
    tasks,
    isLoading,
    error,
    createTask,
    updateTask,
    deleteTask,
  };
}
